/**
 * KAN-230: pure planning logic of the "assign every coupon to K-Photo LaVie" Dev Tool migration.
 * No Firestore access here, so the preview, the apply plan and the safety rules are unit-testable.
 */

export type LavieBrandResolution =
  | { status: 'ok'; brandId: string; brandName: string }
  | { status: 'missing'; message: string }
  | { status: 'ambiguous'; message: string; candidates: { id: string; name: string }[] };

const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Resolves exactly one canonical K-Photo LaVie brand. Names are matched in tiers (exact "K-Photo LaVie" first, then
 * "Photo LaVie", then "LaVie"); the first tier with any match must contain exactly one active brand, otherwise the
 * migration is blocked (never guessing between two brands).
 */
export function resolveCanonicalLavieBrand(
  brands: readonly { id?: string; name?: string; isDeleted?: boolean }[] | null | undefined
): LavieBrandResolution {
  const active = (brands || []).filter(b => b && b.id && b.isDeleted !== true && typeof b.name === 'string');
  const tiers: ((n: string) => boolean)[] = [n => n === 'kphotolavie', n => n === 'photolavie', n => n === 'lavie'];
  for (const tier of tiers) {
    const matches = active.filter(b => tier(norm(b.name!)));
    if (matches.length === 1) return { status: 'ok', brandId: matches[0].id!, brandName: matches[0].name! };
    if (matches.length > 1) {
      return {
        status: 'ambiguous',
        message: `More than one active brand matches K-Photo LaVie (${matches.map(m => `${m.name} [${m.id}]`).join(', ')}). Writes are blocked.`,
        candidates: matches.map(m => ({ id: m.id!, name: m.name! }))
      };
    }
  }
  return { status: 'missing', message: 'No active K-Photo LaVie brand was found in this environment. Writes are blocked.' };
}

export interface CouponMigrationRow {
  id: string;
  status: string;
  isDeleted: boolean;
  before: string[];
  after: string[];
  /** `update` when the scope differs from the target; `skip` when it already equals it (idempotent rerun). */
  action: 'update' | 'skip';
}

export interface CouponMigrationPlan {
  rows: CouponMigrationRow[];
  total: number;
  toUpdate: number;
  toSkip: number;
  /** Total per coupon status (active, redeemed, revoked, expired, ...; `unknown` when missing), deleted ones included. */
  byStatus: Record<string, number>;
  softDeleted: number;
}

const asScope = (v: unknown): string[] => (Array.isArray(v) ? v.filter(x => typeof x === 'string') : []);

export function scopeEquals(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** Every coupon document (active, redeemed, revoked, expired, inactive, soft-deleted) gets exactly `[brandId]`. */
export function planCouponBrandMigration(
  coupons: readonly { id: string; data: Record<string, any> }[],
  brandId: string
): CouponMigrationPlan {
  const target = [brandId];
  const rows: CouponMigrationRow[] = coupons.map(c => {
    const before = asScope(c.data?.['applicableBrands']);
    return {
      id: c.id,
      status: typeof c.data?.['status'] === 'string' && c.data['status'] ? c.data['status'] : 'unknown',
      isDeleted: c.data?.['isDeleted'] === true,
      before,
      after: target,
      action: scopeEquals(before, target) ? 'skip' : 'update'
    };
  });
  const byStatus: Record<string, number> = {};
  rows.forEach(r => (byStatus[r.status] = (byStatus[r.status] || 0) + 1));
  return {
    rows,
    total: rows.length,
    toUpdate: rows.filter(r => r.action === 'update').length,
    toSkip: rows.filter(r => r.action === 'skip').length,
    byStatus,
    softDeleted: rows.filter(r => r.isDeleted).length
  };
}

export function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** The operator confirms the environment by typing its project id; the count and brand are shown beside it. */
export function isMigrationConfirmed(typedProjectId: string, projectId: string, plan: CouponMigrationPlan | null): boolean {
  return !!plan && plan.toUpdate > 0 && !!projectId && typedProjectId.trim() === projectId;
}

export interface CouponMigrationOutcome {
  updated: string[];
  skipped: string[];
  failed: { id: string; error: string }[];
}

/** Applies the plan in bounded batches; a failing batch is recorded and the remaining batches still run. */
export async function applyCouponBrandMigration(
  plan: CouponMigrationPlan,
  commitBatch: (ids: string[]) => Promise<void>,
  batchSize = 400,
  onProgress?: (done: number, total: number) => void
): Promise<CouponMigrationOutcome> {
  const outcome: CouponMigrationOutcome = { updated: [], skipped: plan.rows.filter(r => r.action === 'skip').map(r => r.id), failed: [] };
  const pending = plan.rows.filter(r => r.action === 'update').map(r => r.id);
  let done = 0;
  for (const ids of chunk(pending, batchSize)) {
    try {
      await commitBatch(ids);
      outcome.updated.push(...ids);
    } catch (e: any) {
      const error = (e && e.message) || String(e);
      ids.forEach(id => outcome.failed.push({ id, error }));
    }
    done += ids.length;
    if (onProgress) onProgress(done, pending.length);
  }
  return outcome;
}
